import React, { useRef, useState } from 'react';
import { Pause, Play, Next, Prev, Spkr, Mute, Full, Restore } from '@assets/images/video-player/player-icons';
import { XMarkIcon } from '@heroicons/react/24/solid';

export const GuideVideo = ({ onClose, toggleFullscreen, isFullScreen }: { onClose: any, toggleFullscreen: any, isFullScreen: boolean, }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOverlay, setIsOverlay] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const videoUrl = 'https://static.pw.live/5eb393ee95fab7468a79d189/9a3c6cf5-f26d-4fcf-a2b4-48848c99471f.mp4'

  const togglePlay = () => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        video.play();
        setIsPlaying(true);
      } else {
        video.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !video.muted;
      setIsMuted(video.muted);
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video) {
      const progress = (video.currentTime / video.duration) * 100;
      setProgress(progress);
    }
  };

  const skipTime = (seconds: number) => {
    const video = videoRef.current;
    if (video) {
      video.currentTime += seconds;
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    if (videoRef.current) {
      videoRef.current.volume = vol;
      const isMutedNow = vol === 0;
      videoRef.current.muted = isMutedNow;
      setIsMuted(isMutedNow);
    }
  };

  const toggleOverlay = () => {
    setIsOverlay(!isOverlay)
  };

  // Helper to get background style for range input
  const getRangeBackground = (value: number, min: number, max: number) => {
    const percent = ((value - min) * 100) / (max - min);
    return {
      background: `linear-gradient(to right, #5a4bda 0%, #5a4bda ${percent}%, #e0e0e0 ${percent}%, #e0e0e0 100%)`
    };
  };

  return (
    <div className="w-full relative">
      <style>{`
        input[type='range'].video-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 6px;
          background: transparent;
        }
        input[type='range'].video-slider:focus {
          outline: none;
        }
        /* Webkit browsers */
        input[type='range'].video-slider::-webkit-slider-runnable-track {
          height: 6px;
          background: #e0e0e0;
          border-radius: 3px;
        }
        input[type='range'].video-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 15px;
          height: 15px;
          background: #fff;
          border: 2px solid #5a4bda;
          border-radius: 50%;
          cursor: pointer;
          margin-top: -5px;
          box-shadow: 0 0 2px rgba(0,0,0,0.2);
        }
        input[type='range'].video-slider::-webkit-slider-thumb {
          position: relative;
          z-index: 2;
        }
        input[type='range'].video-slider::-webkit-slider-runnable-track {
          background: linear-gradient(to right, #5a4bda 0%, #5a4bda var(--progress, 50%), #e0e0e0 var(--progress, 50%), #e0e0e0 100%);
        }
        /* Firefox */
        input[type='range'].video-slider::-moz-range-thumb {
          width: 15px;
          height: 15px;
          background: #fff;
          border: 2px solid #5a4bda;
          border-radius: 50%;
          cursor: pointer;
        }
        input[type='range'].video-slider::-moz-range-track {
          height: 6px;
          background: #e0e0e0;
          border-radius: 3px;
        }
        input[type='range'].video-slider::-moz-range-progress {
          height: 6px;
          background: #5a4bda;
          border-radius: 3px;
        }
        /* IE */
        input[type='range'].video-slider::-ms-fill-lower {
          background: #5a4bda;
          border-radius: 3px;
        }
        input[type='range'].video-slider::-ms-fill-upper {
          background: #e0e0e0;
          border-radius: 3px;
        }
        input[type='range'].video-slider::-ms-thumb {
          width: 15px;
          height: 15px;
          background: #fff;
          border: 2px solid #5a4bda;
          border-radius: 50%;
          cursor: pointer;
        }
        input[type='range'].video-slider:focus::-ms-fill-lower {
          background: #5a4bda;
        }
        input[type='range'].video-slider:focus::-ms-fill-upper {
          background: #e0e0e0;
        }
        /* Remove outline for Firefox */
        input[type='range'].video-slider::-moz-focus-outer {
          border: 0;
        }
      `}</style>
      <div className={`absolute w-full h-full cursor-pointer ${isOverlay ? 'bg-[linear-gradient(to_bottom,_#000000_0%,_#55555500_25%,_#55555500_75%,_#000000_100%)]' : 'bg-[#00000000]'} `}
        onClick={toggleOverlay}>
      </div>
      <video
        ref={videoRef}
        onTimeUpdate={handleTimeUpdate}
        className="w-full aspect-video"
        onClick={toggleOverlay}
        src={videoUrl}
      />
      {/* Controls */}
      {isOverlay &&
        <>
          <XMarkIcon className="h-10 w-10 absolute top-6 right-6 text-white cursor-pointer"
            onClick={() => onClose()} />
          <div className="absolute bottom-0 w-full text-white px-4 py-2 flex flex-col ">
            <input
              type="range"
              min={0}
              max={100}
              value={progress}
              onChange={() => { }}
              className="w-full mb-2 video-slider border-none focus:outline-none focus-visible:outline rounded-full"
              style={{
                '--progress': `${progress}%`
              } as React.CSSProperties}
            />
            <div className="flex justify-between items-center">
              <div className="flex gap-3 items-center">

                <button onClick={togglePlay}>
                  {isPlaying ? <Pause /> : <Play />}
                </button>
                <button onClick={() => skipTime(-10)}>
                  <Prev />
                </button>
                <button onClick={() => skipTime(10)}>
                  <Next />
                </button>
                <div className="flex items-center gap-2">
                  <button onClick={handleMute}>
                    {isMuted || volume === 0 ? <Spkr /> : <Mute />}
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-24 video-slider border-none cursor-pointer rounded-full"
                    style={{
                      '--progress': `${volume * 100}%`
                    } as React.CSSProperties}
                  />
                </div>
              </div>
              <button onClick={toggleFullscreen}>
                {isFullScreen ? <Full /> : <Restore />}
              </button>
            </div>
          </div>
        </>
      }

    </div>
  );
};

export default GuideVideo;
